import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import { vestResolver } from "@hookform/resolvers/vest";
import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import CheckboxFormField from "components/checkbox-form-field";
import ClinicFormField from "components/clinic-form-field";
import DateStringFormField from "components/date-string-form-field/date-string-form-field.component";
import {
  WARRANTY_QUICK_OPTIONS,
  warrantyQuickOptionHandler
} from "components/date-string-form-field/date-string-form-field.util";
import InputFormField from "components/input-form-field";
import SelectFormField from "components/select-form-field";
import StatusTransitionCell from "components/status-transition-cell";
import { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import { SUPPLIER_PRICE_FIELD_IS_ENABLED } from "constants/feature-flags";
import {
  INVENTORY_ITEM_CONDITIONS,
  INVENTORY_PRODUCT_STATUSES,
  INVENTORY_PRODUCT_STATUS_COLORS,
  INVENTORY_PRODUCT_STATUS_IN_STOCK
} from "constants/inventory-products";
import { EAR_TYPES } from "constants/products";
import { ACCESSORY_PRODUCT_TYPE } from "constants/sales";
import { selectCurrentPractice } from "features/auth/auth-slice";
import ProductCatalogField from "features/patients/sections/patient-hearing-aids/components/product-catalog-field";
import useUserRoles from "hooks/use-user-roles.hook";
import { Clinic } from "services/types/clinics.type";
import { InventoryProductSpecification } from "services/types/inventory-products.type";
import { Product } from "services/types/products.type";
import { isSerializedProduct } from "utils/products.utils";

import InventoryProductSpecificationForm from "../inventory-product-specification-form";
import {
  INVENTORY_PRODUCT_SUB_TYPES,
  INVENTORY_PRODUCT_TYPES,
  productTypeHasSubtype
} from "./constants";
import { inventoryProductValidator } from "./inventory-product-form-validator";

export type InventoryType = "patientProducts" | "clinicStock";

type InventoryProductFormProps = {
  initialValues?: InventoryProductValues;
  onSubmit: (data: InventoryProductValues) => void;
  quantityReadOnly?: boolean;
  onCancel?: () => void;
  inventoryType: InventoryType;
  allowNonStock?: boolean;
  // Optional slot beside the Loanable/Demoable checkboxes (edit page puts Tags here).
  tagsField?: React.ReactNode;
};

export type InventoryProductValues = {
  type: string;
  subtype: string;
  product?: Partial<Product>;
  clinic: Clinic | null | undefined;
  serial_number: string;
  quantity: string;
  available_quantity: string;
  reserved_quantity?: string;
  ordered_quantity?: string;
  status?: number;
  condition?: number | "" | null;
  ear?: "L" | "R" | "";
  manufacturer_warranty_expiration_date: string;
  loss_damage_warranty_expiration_date: string;
  manufacturer_return_date?: string;
  loanable: boolean;
  demoable: boolean;
  specification: InventoryProductSpecification | null;
  checked_in_at: string;
  mfr_invoice_number: string;
  barcode: string;
  supplier_price: string;
};

// A type must be chosen, and for the one type that has subtypes the subtype must be
// chosen too, before we know enough to search the catalog. Was an allowlist of
// hearing aids and supplies, which hid the Product picker entirely for cochlear
// implants.
const isShowProductField = (type: number, subtype: number) => {
  if (!type) {
    return false;
  }
  return productTypeHasSubtype(type) ? Boolean(subtype) : true;
};

export default function InventoryProductForm({
  initialValues,
  onSubmit,
  onCancel,
  quantityReadOnly = false,
  inventoryType,
  allowNonStock = false,
  tagsField
}: InventoryProductFormProps) {
  const { canViewCosts, isAdminOrBusinessOwner, isSunoAdmin } = useUserRoles();
  const currentPractice = useAppSelector(selectCurrentPractice);

  const flags = useFlags();
  const showSupplierPrice = flags[SUPPLIER_PRICE_FIELD_IS_ENABLED];

  const canSeeCostColumn = canViewCosts && showSupplierPrice;

  const defaultValues: InventoryProductValues = useMemo(() => {
    return merge({}, initialValues);
  }, [initialValues]);

  const formMethods = useForm<InventoryProductValues>({
    mode: "onChange",
    resolver: vestResolver(inventoryProductValidator(), undefined, {
      mode: "sync"
    }),
    defaultValues
  });
  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isValid },
    clearErrors
  } = formMethods;
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [isSpecificationValid, setIsSpecificationValid] = useState(true);

  // Explicit tuple type argument: type-only, no runtime change (ENG-2531). With the
  // names inferred, react-hook-form's FieldPathValues evaluates
  // `TPath[K] & FieldPath<InventoryProductValues>`, and tsc relates every path in that
  // union (product -> clinics -> Clinic ...) to every other one. The relation cache
  // overflows V8's Map limit and `yarn type-check` dies with
  // "RangeError: Map maximum size exceeded".
  const [type, subtype, status, quantity] = watch<
    ["type", "subtype", "status", "quantity"]
  >(["type", "subtype", "status", "quantity"]);

  const setStatus = useCallback(
    (code: StatusTransitionCode) => {
      setValue("status", Number(code));
      return Promise.resolve();
    },
    [setValue]
  );

  useEffect(() => {
    if (Number(type) !== ACCESSORY_PRODUCT_TYPE) {
      setValue("subtype", "");
      if (Number(quantity) > 1) {
        setValue("quantity", "1");
      }
    } else if (inventoryType === "patientProducts") {
      setValue("serial_number", "");
    }
  }, [type, quantity, setValue, inventoryType]);

  useEffect(() => {
    clearErrors(["quantity", "serial_number", "subtype"]);
  }, [type, subtype, clearErrors]);

  const copyFromLeftToRight = useCallback(() => {
    const manufacturerWarrantyExpirationDate =
      getValues("manufacturer_warranty_expiration_date") === "Invalid Date"
        ? ""
        : getValues("manufacturer_warranty_expiration_date");
    setValue(
      "loss_damage_warranty_expiration_date",
      manufacturerWarrantyExpirationDate,
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
  }, [getValues, setValue]);

  const showProductField = isShowProductField(Number(type), Number(subtype));
  const showBarcodeField = Boolean(
    currentPractice?.products_use_barcode_scanner
  );

  return (
    <Box id="inventory-product-form">
      <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
        <Grid item xs={3}>
          <SelectFormField
            name="type"
            label="Type"
            control={control}
            options={INVENTORY_PRODUCT_TYPES.getOptions()}
            required
          />
        </Grid>
        <Grid item xs={3}>
          {productTypeHasSubtype(type) && (
            <SelectFormField
              name="subtype"
              label="Subtype"
              control={control}
              options={INVENTORY_PRODUCT_SUB_TYPES.getOptions()}
              required
            />
          )}
        </Grid>
        <Grid item xs={3}>
          {showProductField && (
            <ProductCatalogField
              required
              control={control}
              name="product"
              label="Product"
              placeholder="Search product by description"
              type={Number(type) ?? 0}
              subtype={Number(subtype) || undefined}
              filters={{
                ...(allowNonStock ? {} : { managed_stock: true }),
                is_active: true
              }}
              allowInventoryStock={false}
            />
          )}
        </Grid>
        <Grid item xs={3}>
          <ClinicFormField name="clinic" control={control} required sx={{}} />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
        <Grid item xs={3}>
          <InputFormField
            required={isSerializedProduct(Number(type), Number(subtype), false)}
            name="serial_number"
            label="Serial Number"
            control={control}
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={3}>
          <InputFormField
            name="available_quantity"
            label={isSunoAdmin ? "Available Quantity" : "Quantity"}
            control={control}
            type="number"
            sx={{ ml: 0 }}
            required={!quantityReadOnly && !isSunoAdmin}
            disabled={quantityReadOnly || isSunoAdmin}
          />
        </Grid>
        {canSeeCostColumn && (
          <Grid item xs={3}>
            <InputFormField
              startAdornment="$"
              control={control}
              name="supplier_price"
              label="cost"
              type="number"
              isMoney
              sx={{ ml: 0 }}
            />
          </Grid>
        )}
        <Grid item xs={3}>
          <SelectFormField
            name="condition"
            label="Condition"
            control={control}
            options={INVENTORY_ITEM_CONDITIONS.getOptions()}
          />
        </Grid>
        <Grid item xs={3}>
          <DateStringFormField
            name="checked_in_at"
            label="Check In"
            control={control}
            sx={{ pr: 0, "& > div": { flexGrow: 1 } }}
          />
        </Grid>
        {isAdminOrBusinessOwner && (
          <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
            <StatusTransitionCell
              testId="inventory-product-status-transition-cell"
              status={status || INVENTORY_PRODUCT_STATUS_IN_STOCK}
              statusColors={INVENTORY_PRODUCT_STATUS_COLORS}
              allStatuses={INVENTORY_PRODUCT_STATUSES}
              onChange={setStatus}
            />
          </Grid>
        )}
      </Grid>
      <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
        <Grid item xs={2}>
          <SelectFormField
            key="ear"
            options={EAR_TYPES.getOptions()}
            name="ear"
            label="Ear"
            control={control}
            sx={{ m: 0 }}
          />
        </Grid>
        <Grid item xs={2}>
          <InputFormField
            control={control}
            label="MFR Invoice Number"
            name="mfr_invoice_number"
            sx={{ m: 0 }}
          />
        </Grid>
        <Grid item xs={2.5}>
          <DateStringFormField
            name="manufacturer_warranty_expiration_date"
            label="MFR Repair Warranty"
            control={control}
            quickOptions={WARRANTY_QUICK_OPTIONS}
            quickOptionsPlacement="below"
            onQuickOptionClick={warrantyQuickOptionHandler}
          />
        </Grid>
        <Grid
          item
          xs={1}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <Tooltip title="Copy MFR Repair Warranty date to Loss and Damages Warranty date">
            <IconButton onClick={copyFromLeftToRight}>
              <ArrowRightAltIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item xs={2.5}>
          <DateStringFormField
            name="loss_damage_warranty_expiration_date"
            label="Loss and Damages Warranty"
            control={control}
            quickOptions={WARRANTY_QUICK_OPTIONS}
            quickOptionsPlacement="below"
            onQuickOptionClick={warrantyQuickOptionHandler}
          />
        </Grid>

        <Grid item xs={2}>
          <DateStringFormField
            name="manufacturer_return_date"
            label="Manufacturer Return Date"
            control={control}
          />
        </Grid>

        {showBarcodeField && (
          <Grid item xs={3}>
            <InputFormField
              name="barcode"
              label="Barcode"
              control={control}
              sx={{ ml: 0 }}
            />
          </Grid>
        )}
        {tagsField && (
          <Grid item xs={4}>
            {tagsField}
          </Grid>
        )}
        <Grid item xs={1}>
          <FormControlLabel
            label="Loanable"
            control={
              <CheckboxFormField
                name="loanable"
                control={control}
                testId="loanable"
              />
            }
          />
        </Grid>
        <Grid item xs={1}>
          <FormControlLabel
            label="Demoable"
            control={
              <CheckboxFormField
                name="demoable"
                control={control}
                testId="demoable"
              />
            }
          />
        </Grid>
        <Grid item container>
          <InventoryProductSpecificationForm
            initialValues={initialValues?.specification}
            type={type}
            subtype={subtype}
            sx={{ maxWidth: "75%" }}
            onChange={(data, isValid) => {
              setValue("specification", data);
              setIsSpecificationValid(isValid);
            }}
            inventoryType={inventoryType}
          />
        </Grid>
        {isSunoAdmin && (
          <Grid item xs={12} container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={3}>
              <InputFormField
                name="quantity"
                label="Quantity"
                control={control}
                type="number"
                sx={{ ml: 0 }}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="reserved_quantity"
                label="Reserved Quantity"
                control={control}
                type="number"
                sx={{ ml: 0 }}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="ordered_quantity"
                label="Ordered Quantity"
                control={control}
                type="number"
                sx={{ ml: 0 }}
                required
              />
            </Grid>
            <Grid item xs={6}></Grid>
          </Grid>
        )}
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ mr: 2, minWidth: "7rem" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || !isSpecificationValid}
          onClick={handleSubmit(onSubmit)}
          sx={{ minWidth: "7rem" }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}
